// ============================================
// CONTEXTO DE AUTENTICAÇÃO ADMIN
// src/contexts/AdminAuthContext.tsx
// ============================================

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../services/staticDataConfig';

// Interfaces
interface PermissoesAdmin {
  visualizarDashboard: boolean;
  gerenciarEmpresas: boolean;
  gerenciarUsuarios: boolean;
  resetarSenhas: boolean;
  visualizarLogs: boolean;
  gerenciarAdmins: boolean;
  exportarDados: boolean;
}

interface AdminUser {
  id: number;
  nome: string;
  usuario: string;
  email: string;
  nivelAcesso: 'super_admin' | 'admin' | 'suporte';
  nivel: 'super_admin' | 'admin' | 'suporte'; // Alias para nivelAcesso
  permissoes: PermissoesAdmin;
}

interface AdminAuthContextData {
  admin: AdminUser | null;
  token: string | null;
  loading: boolean;
  erro: string | null;
  login: (usuario: string, senha: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: () => boolean;
  temPermissao: (permissao: keyof PermissoesAdmin) => boolean;
  hasPermission: (permissao: keyof PermissoesAdmin | string) => boolean; // Alias para temPermissao
  limparErro: () => void;
}

// Contexto
const AdminAuthContext = createContext<AdminAuthContextData>({} as AdminAuthContextData);

// Provider
export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const navigate = useNavigate();

  // Carregar dados do sessionStorage na inicialização
  useEffect(() => {
    const storedToken = sessionStorage.getItem('admin_token');
    const storedAdmin = sessionStorage.getItem('admin_user');

    if (storedToken && storedAdmin) {
      try {
        setToken(storedToken);
        setAdmin(JSON.parse(storedAdmin));
      } catch (e) {
        console.error('[AdminAuth] Erro ao carregar dados:', e);
        sessionStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_user');
      }
    }

    setLoading(false);
  }, []);

  // Função de login
  const login = useCallback(async (usuario: string, senha: string): Promise<boolean> => {
    setLoading(true);
    setErro(null);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuario, senha })
      });

      const data = await response.json();

      if (!response.ok || !data.sucesso) {
        setErro(data.mensagem || 'Erro ao fazer login');
        setLoading(false);
        return false;
      }

      // Garantir que nivel seja um alias de nivelAcesso
      const adminData = {
        ...data.admin,
        nivel: data.admin.nivelAcesso || data.admin.nivel
      };

      // Salvar dados
      setToken(data.token);
      setAdmin(adminData);

      sessionStorage.setItem('admin_token', data.token);
      sessionStorage.setItem('admin_user', JSON.stringify(adminData));

      console.log('[AdminAuth] Login bem-sucedido:', data.admin.usuario);
      setLoading(false);
      
      return true;
    } catch (error: any) {
      console.error('[AdminAuth] Erro no login:', error);
      setErro(error.message || 'Erro ao conectar com o servidor');
      setLoading(false);
      return false;
    }
  }, []);

  // Função de logout
  const logout = useCallback(async () => {
    try {
      // Chamar API de logout
      if (token) {
        await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {}); // Ignorar erros no logout
      }
    } catch (e) {
      console.error('[AdminAuth] Erro no logout:', e);
    }

    // Limpar dados locais
    setToken(null);
    setAdmin(null);
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');

    navigate('/login-admin');
  }, [token, navigate]);

  // Verificar autenticação
  const isAuthenticated = useCallback((): boolean => {
    return !!token && !!admin;
  }, [token, admin]);

  // Verificar permissão específica
  const temPermissao = useCallback((permissao: keyof PermissoesAdmin): boolean => {
    if (!admin) return false;
    
    // Super admin tem todas as permissões
    if (admin.nivelAcesso === 'super_admin') return true;
    
    return admin.permissoes?.[permissao] === true;
  }, [admin]);

  // Alias para temPermissao (compatibilidade com nome em inglês)
  const hasPermission = useCallback((permissao: keyof PermissoesAdmin | string): boolean => {
    if (!admin) return false;
    
    // Super admin tem todas as permissões
    if (admin.nivelAcesso === 'super_admin') return true;

    // Mapear nomes em snake_case para camelCase
    const mapeamento: Record<string, keyof PermissoesAdmin> = {
      'visualizar_dashboard': 'visualizarDashboard',
      'gerenciar_empresas': 'gerenciarEmpresas',
      'gerenciar_usuarios': 'gerenciarUsuarios',
      'resetar_senhas': 'resetarSenhas',
      'visualizar_logs': 'visualizarLogs',
      'gerenciar_admins': 'gerenciarAdmins',
      'exportar_dados': 'exportarDados'
    };

    const permissaoMapeada = mapeamento[permissao] || permissao as keyof PermissoesAdmin;
    
    return admin.permissoes?.[permissaoMapeada] === true;
  }, [admin]);

  // Limpar erro
  const limparErro = useCallback(() => {
    setErro(null);
  }, []);

  return (
    <AdminAuthContext.Provider value={{
      admin,
      token,
      loading,
      erro,
      login,
      logout,
      isAuthenticated,
      temPermissao,
      hasPermission,
      limparErro
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

// Hook para usar o contexto
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  
  if (!context) {
    throw new Error('useAdminAuth deve ser usado dentro de AdminAuthProvider');
  }
  
  return context;
}

// Componente de rota protegida
interface ProtectedAdminRouteProps {
  children: React.ReactNode;
  requiredPermission?: keyof PermissoesAdmin;
  requiredLevel?: ('super_admin' | 'admin' | 'suporte')[];
}

export function ProtectedAdminRoute({ 
  children, 
  requiredPermission,
  requiredLevel 
}: ProtectedAdminRouteProps) {
  const { isAuthenticated, loading, admin, temPermissao } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated()) {
        navigate('/login-admin');
        return;
      }

      // Verificar nível de acesso
      if (requiredLevel && admin && !requiredLevel.includes(admin.nivelAcesso)) {
        navigate('/admin/dashboard');
        return;
      }

      // Verificar permissão específica
      if (requiredPermission && !temPermissao(requiredPermission)) {
        navigate('/admin/dashboard');
        return;
      }
    }
  }, [loading, isAuthenticated, admin, requiredLevel, requiredPermission, temPermissao, navigate]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return null;
  }

  return <>{children}</>;
}

export default AdminAuthContext;
